import { ParsedTransactionData } from '../types';

interface ImagePayload {
  data: string; // base64
  mimeType: string;
}

export const parseExpenseTextWithGroq = async (text: string, images?: ImagePayload[]): Promise<ParsedTransactionData | null> => {
  if (!process.env.GROQ_API_KEY) {
    console.warn("GROQ_API_KEY is not set. Skipping Groq.");
    return null;
  }

  try {
    const prompt = `
      あなたは家計簿のアシスタントです。
      ${images && images.length > 0 ? '画像とテキストから' : 'テキストから'}取引情報を抽出してください。

      【抽出ルール】
      1. **種類 (type)**: 'expense', 'income', 'charge', 'point'。
         - 「PayPayポイントGET」「dポイントで支払い」などは point。
         - チャージの場合は必ず 'charge' としてください。
      2. **金額 (amount)**: 合計金額（数値のみ）。
      3. **場所 (place)**: 
         - レシートの店名を正確に読み取ってください（例: "セブンイレブン", "スターバックス"）。
         - ポイントの場合は"ポイント"。
         - チャージの場合は"チャージ"。
      4. **日付 (date)**: YYYY-MM-DD。
      5. **時間 (time)**: HH:mm (24時間表記)。レシートの印字時刻を正確に抽出してください。
      6. **支払方法 (method)**: 
         - レシートから支払方法（現金, クレジットカード, 電子マネー, QR決済, 銀行振込など）を特定してください。
         - Kyashなどのプリペイドカードは "プリペイドカード" としてください。
      7. **支払詳細 (paymentDetail)**:
         - 以下のリストから最も適切なものを選択してください。
         - 銀行: 三菱UFJ銀行, 三井住友銀行, みずほ銀行, ゆうちょ銀行, 楽天銀行, 住信SBIネット銀行, PayPay銀行, ソニー銀行
         - カード: 楽天カード, 三井住友カード, dカード, au PAY カード, PayPayカード, イオンカード, エポスカード, JCBカード, AMEX
         - 電子マネー: Suica, PASMO, ICOCA, WAON, nanaco, QUICPay, iD, 楽天Edy
         - QR: PayPay, d払い, 楽天ペイ, au PAY, メルペイ, LINE Pay
         - プリペイド: Kyash, au PAY プリペイド, dカード プリペイド, バンドルカード, ソフトバンクカード
      8. **チャージ元 (sourceMethod / sourcePaymentDetail)**:
         - typeが 'charge' の場合のみ抽出。
         - sourceMethod: 銀行振込, クレジットカード, 現金など。
         - sourcePaymentDetail: 上記の支払詳細リストと同様に具体的な名称（例: "三菱UFJ銀行", "楽天カード"）。
      9. **カテゴリ (category)**: 費目。pointの場合は "ポイント"。chargeの場合は "チャージ"。
      10. **購入品目 (items)**:
         - レシートに含まれる個別の商品名と価格をリストで抽出してください。
         - 形式: [{ "name": "商品名", "amount": 数値 }]
         - 割引などはマイナスの価格で表現しても構いません。
      11. **ポイント情報**:
         - pointProvider: (PayPayポイント, dポイント, 楽天ポイント, Pontaポイント, Vポイント, その他)
         - isPointUsage: true(利用/減), false(獲得/増)
      12. **メモ (note)**: 購入品目などの要約。

      【入力】
      テキスト: "${text}"
      
      JSON形式で返してください。
      出力キー: type, amount, place, date, time, method, paymentDetail, sourceMethod, sourcePaymentDetail, category, items, pointProvider, isPointUsage, note
    `;

    let messages: any[] = [];
    let model = 'llama-3.3-70b-versatile';

    if (images && images.length > 0) {
      // Use the new supported vision model
      model = 'llama-3.2-90b-vision-preview'; // Fallback or check if this works, but docs say llama-4-scout...
      // Wait, the user provided docs say: meta-llama/llama-4-scout-17b-16e-instruct
      model = 'meta-llama/llama-4-scout-17b-16e-instruct';
      
      const content: any[] = [{ type: 'text', text: prompt }];
      
      images.forEach(img => {
        content.push({
          type: 'image_url',
          image_url: {
            url: `data:${img.mimeType};base64,${img.data}`
          }
        });
      });

      messages = [{ role: 'user', content }];
    } else {
      messages = [{ role: 'user', content: prompt }];
    }

    const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${process.env.GROQ_API_KEY}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        messages,
        model,
        temperature: 0,
        response_format: { type: "json_object" }
      })
    });

    if (!response.ok) {
      throw new Error(`Groq API error: ${response.statusText}`);
    }

    const json = await response.json();
    const content = json.choices[0]?.message?.content;

    if (content) {
      return JSON.parse(content) as ParsedTransactionData;
    }
    return null;

  } catch (error) {
    console.error("Groq parse error:", error);
    return null; // Return null to allow fallback to Gemini
  }
};